/**
 * Lead Details Modal
 * Ficha do lead: indicadores, contato, mudança de estágio, conversão e exclusão.
 */

import React, { useState } from 'react';
import { Building2, Loader2, Mail, Phone, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { Lead } from '../../types/leads';
import { LeadStage, LEAD_PRIORITY_LABELS, LEAD_SOURCE_LABELS, LEAD_STAGE_LABELS } from '../../types/leads';
import { leadsService } from '../../services/leads-service';
import { ConvertToClientModal } from './ConvertToClientModal';
import { ACTIVE_LEAD_STAGES, formatLeadValue, leadCreatedAgo } from './lead-format';

interface LeadDetailsModalProps {
  lead: Lead;
  onClose: () => void;
  onUpdate: () => Promise<void>;
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-muted/40 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{children}</div>
    </div>
  );
}

export const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({ lead, onClose, onUpdate }) => {
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [changingStage, setChangingStage] = useState<LeadStage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleStageChange = async (newStage: LeadStage) => {
    if (newStage === lead.stage) return;
    try {
      setChangingStage(newStage);
      await leadsService.updateStage(lead.id, { stage: newStage });
      await onUpdate();
      toast.success('Estágio atualizado!');
    } catch (error) {
      console.error('Erro ao atualizar estágio:', error);
      toast.error('Erro ao atualizar estágio');
    } finally {
      setChangingStage(null);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await leadsService.delete(lead.id);
      toast.success('Lead excluído com sucesso!');
      await onUpdate();
      onClose();
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      toast.error('Erro ao excluir lead');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const isActive = lead.stage !== LeadStage.CONVERTIDO && lead.stage !== LeadStage.PERDIDO;

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent size="xl" fullScreenOnMobile className="max-h-[90vh] overflow-y-auto max-sm:max-h-dvh">
          <DialogHeader>
            <DialogTitle className="text-xl">{lead.name}</DialogTitle>
            <DialogDescription>
              Lead #{lead.leadNumber} · {LEAD_STAGE_LABELS[lead.stage]}
            </DialogDescription>
            <div className="flex flex-wrap gap-1.5">
              {lead.isHotLead && <Badge variant="destructive">Quente</Badge>}
              {lead.isQualified && <Badge variant="secondary">Qualificado</Badge>}
            </div>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Score">
              {lead.score}/100
              <div className="mt-1 h-1.5 w-full rounded-full bg-muted" aria-hidden="true">
                <div className="h-1.5 rounded-full bg-primary" style={{ width: `${Math.min(Math.max(lead.score, 0), 100)}%` }} />
              </div>
            </Stat>
            <Stat label="Valor estimado">{formatLeadValue(lead.estimatedValue) ?? 'N/A'}</Stat>
            <Stat label="Prioridade">{LEAD_PRIORITY_LABELS[lead.priority] ?? lead.priority}</Stat>
            <Stat label="Estágio">{LEAD_STAGE_LABELS[lead.stage]}</Stat>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <section className="flex flex-col gap-1.5 text-sm">
              <h3 className="font-semibold">Contato</h3>
              {lead.email && (
                <p className="flex items-center gap-2">
                  <Mail className="size-4 text-muted-foreground" />
                  <a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a>
                </p>
              )}
              {lead.whatsapp && (
                <p className="flex items-center gap-2">
                  <Phone className="size-4 text-muted-foreground" /> {lead.whatsapp}
                </p>
              )}
              {lead.companyName && (
                <p className="flex items-center gap-2">
                  <Building2 className="size-4 text-muted-foreground" /> {lead.companyName}
                </p>
              )}
              {!lead.email && !lead.whatsapp && !lead.companyName && <p className="text-muted-foreground">Sem contato informado.</p>}
            </section>

            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Origem</dt>
              <dd>{LEAD_SOURCE_LABELS[lead.source]}</dd>
              {lead.legalArea && (
                <>
                  <dt className="text-muted-foreground">Área jurídica</dt>
                  <dd>{lead.legalArea}</dd>
                </>
              )}
              <dt className="text-muted-foreground">Criado</dt>
              <dd>{leadCreatedAgo(lead)}</dd>
              {lead.assignedToName && (
                <>
                  <dt className="text-muted-foreground">Responsável</dt>
                  <dd>{lead.assignedToName}</dd>
                </>
              )}
            </dl>
          </div>

          {lead.notes && (
            <section className="flex flex-col gap-1.5 text-sm">
              <h3 className="font-semibold">Notas</h3>
              <p className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3">{lead.notes}</p>
            </section>
          )}

          {isActive && (
            <section className="flex flex-col gap-1.5 text-sm">
              <h3 className="font-semibold">Mudar estágio</h3>
              <div className="flex flex-wrap gap-2">
                {ACTIVE_LEAD_STAGES.map((stage) => (
                  <Button
                    key={stage}
                    size="sm"
                    variant={stage === lead.stage ? 'default' : 'outline'}
                    aria-pressed={stage === lead.stage}
                    disabled={changingStage !== null || stage === lead.stage}
                    onClick={() => handleStageChange(stage)}
                  >
                    {changingStage === stage && <Loader2 className="animate-spin" />}
                    {LEAD_STAGE_LABELS[stage]}
                  </Button>
                ))}
              </div>
            </section>
          )}

          <DialogFooter className="sm:justify-between">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-destructive">Tem certeza?</span>
                <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting}>
                  {deleting ? <Loader2 className="animate-spin" /> : <Trash2 />} Confirmar
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 /> Excluir lead
              </Button>
            )}
            <div className="flex gap-2">
              {isActive && (
                <Button onClick={() => setShowConvertModal(true)}>
                  <UserPlus /> Converter em cliente
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>Fechar</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showConvertModal && (
        <ConvertToClientModal
          lead={lead}
          onClose={() => setShowConvertModal(false)}
          onSuccess={async () => {
            setShowConvertModal(false);
            await onUpdate();
            toast.success('Lead convertido em cliente com sucesso!');
            setTimeout(() => onClose(), 1000);
          }}
        />
      )}
    </>
  );
};

export default LeadDetailsModal;
